import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import TextField, { TextFieldProps } from '../TextField/TextField';

const messages = defineMessages({
  invalidDate: {
    id: 'Please enter a valid date.',
    defaultMessage: 'Please enter a valid date.',
  },
});

export interface DateValue {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

export interface DatePickerProps extends Omit<TextFieldProps, 'onChange'> {
  /**
   * Whether to only input a date.
   */
  isDateOnly?: boolean;
  /**
   * The onChange handler.
   */
  onChange?: (value?: DateValue) => void;
}

const DatePicker = ({
  isDateOnly,
  onChange,
  value,
  type,
  errorMessage,
  isInvalid,
  ...props
}: DatePickerProps) => {
  const intl = useIntl();
  // Raw input of a date the form cannot store; kept so the field shows what
  // was typed and the typo can be fixed instead of retyping the whole date.
  const [invalidInput, setInvalidInput] = useState<string>();
  const isOutOfRange = invalidInput !== undefined;
  return (
    <TextField
      type={isDateOnly ? 'date' : 'datetime-local'}
      // Keeps Chrome from accepting a fifth year digit in the first place.
      max={isDateOnly ? '9999-12-31' : '9999-12-31T23:59'}
      value={invalidInput ?? (value || '')}
      errorMessage={
        errorMessage ||
        (isOutOfRange ? intl.formatMessage(messages.invalidDate) : '')
      }
      isInvalid={isInvalid || isOutOfRange}
      onChange={(value) => {
        if (!onChange) {
          return;
        }
        setInvalidInput(undefined);
        if (!value) {
          onChange();
          return;
        }
        const date = new Date(value);
        // Browsers ignoring max accept up to six year digits, which the form's
        // ISO parser rejects. Store nothing rather than keep a value the field
        // no longer shows, and tell the user why.
        if (Number.isNaN(date.getTime()) || date.getFullYear() > 9999) {
          setInvalidInput(value);
          onChange();
          return;
        }
        onChange({
          year: date.getFullYear(),
          month: date.getMonth() + 1,
          day: date.getDate(),
          hour: date.getHours(),
          minute: date.getMinutes(),
          second: date.getSeconds(),
        } as any);
      }}
      {...props}
    />
  );
};

export default DatePicker;
